import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { TrustedStaticHtmlPipe } from '../../../../shared/pipe/trusted-static-html.pipe';
import { BrowserPlatformService } from '../../../../shared/services/browser-platform.service';
import {
  AuthService,
  MaintenancePermissionSection,
  SYSTEM_SECTIONS,
} from '../../../services/auth.service';
import { LaunchPhaseService } from '../../../services/launch-phase.service';
import { SidebarService } from '../../../services/sidebar.service';
import { ReturnNavigationService } from '../../../services/return-navigation.service';

type NavItem = {
  name: string;
  icon: string;
  enabled?: boolean;
  path?: string;
  sectionId?: number;
  maintenanceSection?: MaintenancePermissionSection;
  adminOnly?: boolean;
  subItems?: NavSubItem[];
  promotedFrom?: string;
};

type NavSubItem = {
  name: string;
  path: string;
  enabled?: boolean;
  sectionId?: number;
  maintenanceSection?: MaintenancePermissionSection;
  adminOnly?: boolean;
  compactName?: string;
};

type SidebarTooltip = {
  label: string;
  left: number;
  top: number;
};

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, TrustedStaticHtmlPipe],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {
  private auth = inject(AuthService);
  private browser = inject(BrowserPlatformService);
  private launchPhases = inject(LaunchPhaseService);
  private returnNavigation = inject(ReturnNavigationService);

  // Main nav items
  navItems: NavItem[] = [
    {
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 14.1526 4.3002 16.1184 5.61936 17.616C6.17279 15.3096 8.24852 13.5955 10.7246 13.5955H13.2746C15.7509 13.5955 17.8268 15.31 18.38 17.6167C19.6996 16.119 20.5 14.153 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5ZM17.0246 18.8566V18.8455C17.0246 16.7744 15.3457 15.0955 13.2746 15.0955H10.7246C8.65354 15.0955 6.97461 16.7744 6.97461 18.8455V18.856C8.38223 19.8895 10.1198 20.5 12 20.5C13.8798 20.5 15.6171 19.8898 17.0246 18.8566ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.9991 7.25C10.8847 7.25 9.98126 8.15342 9.98126 9.26784C9.98126 10.3823 10.8847 11.2857 11.9991 11.2857C13.1135 11.2857 14.0169 10.3823 14.0169 9.26784C14.0169 8.15342 13.1135 7.25 11.9991 7.25ZM8.48126 9.26784C8.48126 7.32499 10.0563 5.75 11.9991 5.75C13.9419 5.75 15.5169 7.32499 15.5169 9.26784C15.5169 11.2107 13.9419 12.7857 11.9991 12.7857C10.0563 12.7857 8.48126 11.2107 8.48126 9.26784Z" fill="currentColor"></path>
    </svg>`,
      name: 'Users',
      path: '/users',
      sectionId: SYSTEM_SECTIONS.users,
    },
    {
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.75 14.6667C12.75 14.2524 13.0858 13.9167 13.5 13.9167H16.5C16.9142 13.9167 17.25 14.2524 17.25 14.6667C17.25 15.0809 16.9142 15.4167 16.5 15.4167H13.5C13.0858 15.4167 12.75 15.0809 12.75 14.6667Z" fill="currentColor"/>
      <path d="M13.5 8.58334C13.0858 8.58334 12.75 8.91913 12.75 9.33334C12.75 9.74756 13.0858 10.0833 13.5 10.0833H16.5C16.9142 10.0833 17.25 9.74756 17.25 9.33334C17.25 8.91913 16.9142 8.58334 16.5 8.58334H13.5Z" fill="currentColor"/>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.5 3.25C10.2574 3.25 9.25 4.25736 9.25 5.5V7.75H5.5C4.25736 7.75 3.25 8.75736 3.25 10V20C3.25 20.4142 3.58579 20.75 4 20.75H20C20.4142 20.75 20.75 20.4142 20.75 20V5.5C20.75 4.25736 19.7426 3.25 18.5 3.25H11.5ZM9.25 19.25V17H7.75586C7.34165 17 7.00586 16.6642 7.00586 16.25C7.00586 15.8358 7.34165 15.5 7.75586 15.5H9.25V13H7.75586C7.34165 13 7.00586 12.6642 7.00586 12.25C7.00586 11.8358 7.34165 11.5 7.75586 11.5H9.25V9.25H5.5C5.08579 9.25 4.75 9.58579 4.75 10V19.25H9.25ZM10.75 12.2773C10.7503 12.2683 10.7505 12.2591 10.7505 12.25C10.7505 12.2409 10.7503 12.2317 10.75 12.2227V5.5C10.75 5.08579 11.0858 4.75 11.5 4.75H18.5C18.9142 4.75 19.25 5.08579 19.25 5.5V19.25H10.75V16.2773C10.7503 16.2683 10.7505 16.2591 10.7505 16.25C10.7505 16.2409 10.7503 16.2317 10.75 16.2227V12.2773Z" fill="currentColor"/>
    </svg>`,
      name: 'Customers',
      path: '/group',
      sectionId: SYSTEM_SECTIONS.group,
    },
    {
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-5-4v4h4V3h-4Z"/>
    </svg>`,
      name: 'Applications',
      path: '/application',
      sectionId: SYSTEM_SECTIONS.application,
    },
    {
      name: 'Recommendations',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M5.5 3.25C4.25736 3.25 3.25 4.25736 3.25 5.5V18.5C3.25 19.7426 4.25736 20.75 5.5 20.75H18.5001C19.7427 20.75 20.7501 19.7426 20.7501 18.5V5.5C20.7501 4.25736 19.7427 3.25 18.5001 3.25H5.5ZM4.75 5.5C4.75 5.08579 5.08579 4.75 5.5 4.75H18.5001C18.9143 4.75 19.2501 5.08579 19.2501 5.5V18.5C19.2501 18.9142 18.9143 19.25 18.5001 19.25H5.5C5.08579 19.25 4.75 18.9142 4.75 18.5V5.5ZM6.25005 9.7143C6.25005 9.30008 6.58583 8.9643 7.00005 8.9643L17 8.96429C17.4143 8.96429 17.75 9.30008 17.75 9.71429C17.75 10.1285 17.4143 10.4643 17 10.4643L7.00005 10.4643C6.58583 10.4643 6.25005 10.1285 6.25005 9.7143ZM6.25005 14.2857C6.25005 13.8715 6.58583 13.5357 7.00005 13.5357H17C17.4143 13.5357 17.75 13.8715 17.75 14.2857C17.75 14.6999 17.4143 15.0357 17 15.0357H7.00005C6.58583 15.0357 6.25005 14.6999 6.25005 14.2857Z" fill="currentColor"></path>
    </svg>`,
      path: '/recommendations',
      sectionId: SYSTEM_SECTIONS.recommendations,
    },
    {
      name: 'Audits',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M5.52344 3.25C4.2808 3.25 3.27344 4.25736 3.27344 5.5V18.5C3.27344 19.7426 4.2808 20.75 5.52344 20.75H18.5234C19.7661 20.75 20.7734 19.7426 20.7734 18.5V5.5C20.7734 4.25736 19.7661 3.25 18.5234 3.25H5.52344ZM4.77344 5.5C4.77344 5.08579 5.10922 4.75 5.52344 4.75H18.5234C18.9377 4.75 19.2734 5.08579 19.2734 5.5V8.58301L4.77344 8.58301V5.5ZM4.77344 10.083V18.5C4.77344 18.9142 5.10922 19.25 5.52344 19.25H8.60645L8.60645 10.083H4.77344ZM10.1064 10.083V13.916H19.2734V10.083L10.1064 10.083ZM10.1064 15.416H19.2734V18.5C19.2734 18.9142 18.9377 19.25 18.5234 19.25H10.1064V15.416Z" fill="currentColor"/>
    </svg>`,
      sectionId: SYSTEM_SECTIONS.audits,
      subItems: [
        { name: 'All', path: '/audits', sectionId: SYSTEM_SECTIONS.audits },
        { name: 'TBP', path: '/audit/planning', sectionId: SYSTEM_SECTIONS.auditPlanning },
        { name: 'CWC', path: '/audit/confirmed', sectionId: SYSTEM_SECTIONS.auditsConfirmed },
      ],
    },
    {
      name: 'Raised Invoices',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 3v4a1 1 0 0 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1ZM8 12v6h8v-6H8Z"/>
      </svg>`,
      path: '/raisedinvoices',
      sectionId: SYSTEM_SECTIONS.raisedInvoices,
    },
    {
      name: 'Non-Compliances',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 14v7M5 4.971v9.541c5.6-5.538 8.4 2.64 14-.086v-9.54C13.4 7.61 10.6-.568 5 4.97Z"/>
</svg>
      `,
      path: '/noncompliance',
      sectionId: SYSTEM_SECTIONS.nonCompliances,
    },
    {
      name: 'LTQLs',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
    </svg>`,
      path: '/ltqls',
      sectionId: SYSTEM_SECTIONS.ltqls,
    },
    {
      name: 'Complaints',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"/>
    </svg>`,
      path: '/complaints',
      sectionId: SYSTEM_SECTIONS.complaints,
    },
    {
      name: 'Approvals',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m8.032 12 1.984 1.984 4.96-4.96m4.55 5.272.893-.893a1.984 1.984 0 0 0 0-2.806l-.893-.893a1.984 1.984 0 0 1-.581-1.403V7.04a1.984 1.984 0 0 0-1.984-1.984h-1.262a1.983 1.983 0 0 1-1.403-.581l-.893-.893a1.984 1.984 0 0 0-2.806 0l-.893.893a1.984 1.984 0 0 1-1.403.581H7.04A1.984 1.984 0 0 0 5.055 7.04v1.262c0 .527-.209 1.031-.581 1.403l-.893.893a1.984 1.984 0 0 0 0 2.806l.893.893c.372.372.581.876.581 1.403v1.262a1.984 1.984 0 0 0 1.984 1.984h1.262c.527 0 1.031.209 1.403.581l.893.893a1.984 1.984 0 0 0 2.806 0l.893-.893a1.985 1.985 0 0 1 1.403-.581h1.262a1.984 1.984 0 0 0 1.984-1.984V15.7c0-.527.209-1.031.581-1.403Z"/>
    </svg>`,
      path: '/approvals',
      sectionId: SYSTEM_SECTIONS.approval,
    },
    {
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h8m3.526 2.296.893-.893a1.984 1.984 0 0 0 0-2.806l-.893-.893a1.984 1.984 0 0 1-.581-1.403V7.04a1.984 1.984 0 0 0-1.984-1.984h-1.262a1.983 1.983 0 0 1-1.403-.581l-.893-.893a1.984 1.984 0 0 0-2.806 0l-.893.893a1.984 1.984 0 0 1-1.403.581H7.04A1.984 1.984 0 0 0 5.055 7.04v1.262c0 .527-.209 1.031-.581 1.403l-.893.893a1.984 1.984 0 0 0 0 2.806l.893.893c.372.372.581.876.581 1.403v1.262a1.984 1.984 0 0 0 1.984 1.984h1.262c.527 0 1.031.209 1.403.581l.893.893a1.984 1.984 0 0 0 2.806 0l.893-.893a1.985 1.985 0 0 1 1.403-.581h1.262a1.984 1.984 0 0 0 1.984-1.984V15.7c0-.527.209-1.031.581-1.403Z"/>
    </svg>`,
      name: 'Withdrawn Certificates',
      path: '/withdrawn-certificates',
      sectionId: SYSTEM_SECTIONS.withdrawnCertificates,
    },
    {
      name: 'PT Cards',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 9h3m-3 3h3m-3 3h3m-6 1c-.306-.613-.933-1-1.618-1H7.618c-.685 0-1.312.387-1.618 1M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm7 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"/>
    </svg>`,
      path: '/cards',
      sectionId: SYSTEM_SECTIONS.cards,
    },
    {
      name: 'Reporting',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M8.50391 4.25C8.50391 3.83579 8.83969 3.5 9.25391 3.5H15.2777C15.4766 3.5 15.6674 3.57902 15.8081 3.71967L18.2807 6.19234C18.4214 6.333 18.5004 6.52376 18.5004 6.72268V16.75C18.5004 17.1642 18.1646 17.5 17.7504 17.5H16.248V17.4993H14.748V17.5H9.25391C8.83969 17.5 8.50391 17.1642 8.50391 16.75V4.25ZM14.748 19H9.25391C8.01126 19 7.00391 17.9926 7.00391 16.75V6.49854H6.24805C5.83383 6.49854 5.49805 6.83432 5.49805 7.24854V19.75C5.49805 20.1642 5.83383 20.5 6.24805 20.5H13.998C14.4123 20.5 14.748 20.1642 14.748 19.75L14.748 19ZM7.00391 4.99854V4.25C7.00391 3.00736 8.01127 2 9.25391 2H15.2777C15.8745 2 16.4468 2.23705 16.8687 2.659L19.3414 5.13168C19.7634 5.55364 20.0004 6.12594 20.0004 6.72268V16.75C20.0004 17.9926 18.9931 19 17.7504 19H16.248L16.248 19.75C16.248 20.9926 15.2407 22 13.998 22H6.24805C5.00541 22 3.99805 20.9926 3.99805 19.75V7.24854C3.99805 6.00589 5.00541 4.99854 6.24805 4.99854H7.00391Z" fill="currentColor"></path>
      </svg>`,
      path: '/reporting',
      sectionId: SYSTEM_SECTIONS.reporting,
    },
    {
      name: 'Suppliers',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 0 0-2 2v4m5-6h8M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m0 0h3a2 2 0 0 1 2 2v4m0 0v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6m18 0s-4 2-9 2-9-2-9-2m9-2h.01"/>
    </svg>`,
      path: '/suppliers',
      sectionId: SYSTEM_SECTIONS.suppliers,
    },
    {
      name: 'Suppliers Invoicing',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 3v4a1 1 0 0 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1ZM8 12v6h8v-6H8Z"/>
      </svg>`,
      path: '/invoicing/suppliers',
      sectionId: SYSTEM_SECTIONS.suppliersInvoicing,
    },
    {
      name: 'Customer Testing Invoices',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 3v4a1 1 0 0 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7.914a1 1 0 0 1 .293-.707l3.914-3.914A1 1 0 0 1 9.914 3H18a1 1 0 0 1 1 1ZM8 12v6h8v-6H8Z"/>
    </svg>`,
      path: '/invoicing/customer-testing',
      sectionId: SYSTEM_SECTIONS.supplierRaisedInvoices,
    },
    {
      name: 'Travel Advice',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.04 21.9877C15.94 21.9877 15.85 21.9677 15.76 21.9277L11.98 20.3777L8.2 21.9277C7.97 22.0177 7.71 21.9977 7.5 21.8577C7.29 21.7177 7.17 21.4877 7.17 21.2377V19.0577C7.17 18.8277 7.28 18.6077 7.46 18.4677L9.39 16.9677L9.27 14.5077L4.11 15.3277C3.9 15.3677 3.67 15.2977 3.51 15.1577C3.35 15.0177 3.25 14.8077 3.25 14.5877V12.0577C3.25 11.7677 3.42 11.5077 3.68 11.3777L9 8.8577C8.9 6.6077 9.57 4.3577 10.9 2.5377C11.41 1.8377 12.59 1.8377 13.1 2.5377C14.43 4.3577 15.11 6.5977 15 8.8577L20.32 11.3777C20.58 11.4977 20.75 11.7677 20.75 12.0577V14.5877C20.75 14.8077 20.65 15.0177 20.49 15.1577C20.32 15.2977 20.1 15.3677 19.89 15.3277L14.73 14.5077L14.61 16.9677L16.54 18.4677C16.72 18.6077 16.83 18.8277 16.83 19.0577V21.2377C16.83 21.4877 16.71 21.7177 16.5 21.8577C16.37 21.9377 16.23 21.9877 16.08 21.9877H16.04ZM8.67 19.4277V20.1177L11.7 18.8777C11.88 18.7977 12.09 18.7977 12.27 18.8777L15.3 20.1177V19.4177L13.35 17.8977C13.16 17.7477 13.05 17.5077 13.06 17.2677L13.24 13.5877C13.25 13.3777 13.35 13.1777 13.52 13.0477C13.69 12.9177 13.9 12.8577 14.11 12.8877L19.23 13.6977V12.5177L13.89 9.9877C13.62 9.8577 13.45 9.57769 13.46 9.26769L13.48 8.7977C13.57 6.9377 13.04 5.0877 11.98 3.5677C10.92 5.0877 10.39 6.9477 10.48 8.7977L10.5 9.26769C10.52 9.5677 10.35 9.8577 10.07 9.9877L4.73 12.5177V13.6977L9.85 12.8877C10.06 12.8577 10.27 12.9077 10.44 13.0477C10.61 13.1777 10.71 13.3777 10.72 13.5877L10.9 17.2677C10.91 17.5077 10.8 17.7477 10.61 17.8977L8.67 19.4177V19.4277Z" fill="currentColor"/>
    </svg>`,
      path: '/country',
      sectionId: SYSTEM_SECTIONS.countryManagement,
    },
    {
      name: 'Executive Dashboard',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path stroke="currentColor" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5Zm16 14a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2ZM4 13a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6Zm16-2a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6Z"/>
    </svg>`,
      path: '/dashboard',
      sectionId: SYSTEM_SECTIONS.dashboard,
    },
    {
      name: 'Maintenance',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"/>
    </svg>`,
      sectionId: SYSTEM_SECTIONS.maintenance,
      subItems: [
        {
          name: 'Itinerary schedule headers',
          path: '/maintenance/itineraryscheduleheaders',
          maintenanceSection: 'itineraryscheduleheaders',
          compactName: 'Headers',
        },
        {
          name: 'Itinerary schedule items',
          path: '/maintenance/itineraryscheduleitems',
          maintenanceSection: 'itineraryscheduleitems',
          compactName: 'Items',
        },
        {
          name: 'Itinerary sampling items',
          path: '/maintenance/itinerarysamplingitems',
          maintenanceSection: 'itinerarysamplingitems',
          compactName: 'Sample',
        },
        {
          name: 'Auditors',
          path: '/maintenance/auditors',
          maintenanceSection: 'auditors',
          compactName: 'Aud.',
        },
        {
          name: 'Product Standards',
          path: '/maintenance/productstandards',
          maintenanceSection: 'productstandards',
          compactName: 'Stds',
        },
        {
          name: 'Schemes',
          path: '/maintenance/schemes',
          maintenanceSection: 'schemes',
          compactName: 'Schemes',
        },
        {
          name: 'Special Days',
          path: '/maintenance/specialdays',
          maintenanceSection: 'specialdays',
          compactName: 'Days',
        },
        {
          name: 'Lookup Data',
          path: '/maintenance/lookup-data',
          maintenanceSection: 'lookup-data',
          compactName: 'Lookup',
        },
        {
          name: 'MTR Templates',
          path: '/maintenance/mtr-templates',
          maintenanceSection: 'mtr-templates',
          compactName: 'MTRs',
        },
      ],
    },
    {
      name: 'Admin Tools',
      icon: `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.5 6h3m-6 4h9m-7 4h5M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"/>
    </svg>`,
      adminOnly: true,
      subItems: [
        {
          name: 'Login as',
          path: '/admin-tools/users',
          adminOnly: true,
          compactName: 'Login as',
        },
        {
          name: 'Product Labels',
          path: '/admin-tools/product-labels',
          adminOnly: true,
          compactName: 'Labels',
        },
        {
          name: 'LTQL Import',
          path: '/admin-tools/ltql-import',
          adminOnly: true,
          compactName: 'LTQL',
        },
        {
          name: 'Scheduled Tasks',
          path: '/admin-tools/scheduled-tasks',
          adminOnly: true,
          compactName: 'Tasks',
        },
        {
          name: 'Logs',
          path: '/admin-tools/logs',
          adminOnly: true,
          compactName: 'Logs',
        },
        {
          name: 'History',
          path: '/admin-tools/history',
          adminOnly: true,
          compactName: 'History',
        },
        {
          name: 'SharePoint Queue',
          path: '/admin-tools/sharepoint-queue',
          adminOnly: true,
          compactName: 'SharePoint',
        },
        {
          name: 'Outlook Queue',
          path: '/admin-tools/outlook-queue',
          adminOnly: true,
          compactName: 'Outlook',
        },
        {
          name: 'Invoice Recovery',
          path: '/admin-tools/supplier-invoice-email-recovery',
          adminOnly: true,
          compactName: 'Invoice Recovery',
        },
      ],
    },
  ];

  readonly visibleNavItems = computed(() => {
    return this.navItems
      .map((nav) => {
        const canReadParent = this.canReadNavItem(nav);
        const subItems = nav.subItems?.filter((subItem) => this.canReadNavItem(subItem));

        const navWithLaunchState = this.withLaunchState({ ...nav, subItems });

        if (navWithLaunchState.subItems?.length === 1) {
          const child = navWithLaunchState.subItems[0];
          return {
            name: nav.name === 'Audits' ? `Audits - ${child.name}` : child.name,
            path: child.path,
            icon: nav.icon,
            enabled: child.enabled,
            sectionId: child.sectionId,
            maintenanceSection: child.maintenanceSection,
            adminOnly: child.adminOnly,
            promotedFrom: nav.name,
          };
        }

        if (nav.subItems) {
          const canShowGroup =
            nav.sectionId === SYSTEM_SECTIONS.maintenance
              ? !!subItems?.length
              : canReadParent || !!subItems?.length;

          if (canShowGroup) {
            return navWithLaunchState;
          }

          return null;
        }

        return canReadParent ? navWithLaunchState : null;
      })
      .filter((nav): nav is NavItem => !!nav);
  });

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  collapsedTooltip: SidebarTooltip | null = null;
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;

  private sidebarExpanded = false;
  private sidebarMobileOpen = false;
  private subscription: Subscription = new Subscription();

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
  }

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$]).subscribe(
        ([isExpanded, isMobileOpen]) => {
          const wasCompact = !this.sidebarExpanded && !this.sidebarMobileOpen;
          this.sidebarExpanded = isExpanded;
          this.sidebarMobileOpen = isMobileOpen;
          if (isExpanded || isMobileOpen) {
            this.hideCollapsedTooltip();
          }
          if (wasCompact && (isExpanded || isMobileOpen)) {
            this.setActiveMenuFromRoute(this.router.url);
          }
          this.refreshOpenSubmenuHeight();
        },
      ),
    );

    // Subscribe to router events
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      }),
    );

    // Initial load
    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    const currentPath = this.router.url.split(/[?#]/)[0];
    return currentPath === path || currentPath.startsWith(`${path}/`);
  }

  isNavActive(nav: NavItem): boolean {
    if (!nav.path) return false;
    return nav.promotedFrom === 'Audits'
      ? this.isAuditSubItemActiveForPath(nav.path, this.router.url.split(/[?#]/)[0])
      : this.isActive(nav.path);
  }

  isSubItemActive(nav: NavItem, subItem: NavSubItem): boolean {
    if (nav.name === 'Audits') {
      return this.isAuditSubItemActiveForPath(subItem.path, this.router.url.split(/[?#]/)[0]);
    }

    return this.isActive(subItem.path);
  }

  isEnabled(item: { enabled?: boolean }): boolean {
    return item.enabled !== false;
  }

  toggleSubmenu(section: string, index: number) {
    const nav = this.visibleNavItems()[index];
    if (!nav || !this.isEnabled(nav)) {
      return;
    }

    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;
      this.refreshSubmenuHeight(key);
    }
  }

  handleLogoToggle() {
    this.hideCollapsedTooltip();
    if (this.browser.innerWidth >= 1280) {
      this.sidebarService.toggleExpanded();
    } else {
      this.sidebarService.toggleMobileOpen();
    }
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    const currentPath = currentUrl.split(/[?#]/)[0];
    const menuGroups = [{ items: this.visibleNavItems(), prefix: 'main' }];
    let activeSubmenu: { key: string; nav: NavItem } | null = null;

    for (const group of menuGroups) {
      for (let i = 0; i < group.items.length; i++) {
        const nav = group.items[i];
        if (!nav.subItems) {
          continue;
        }

        for (const subItem of nav.subItems) {
          const isSubItemActive =
            nav.name === 'Audits'
              ? this.isAuditSubItemActiveForPath(subItem.path, currentPath)
              : currentPath === subItem.path || currentPath.startsWith(`${subItem.path}/`);

          if (isSubItemActive) {
            activeSubmenu = { key: `${group.prefix}-${i}`, nav };
          }
        }
      }
    }

    if (!this.sidebarExpanded && !this.sidebarMobileOpen) {
      this.openSubmenu =
        activeSubmenu && this.canShowCollapsedSubmenu(activeSubmenu.nav) ? activeSubmenu.key : null;
      return;
    }

    if (activeSubmenu) {
      this.openSubmenu = activeSubmenu.key;
      this.refreshSubmenuHeight(activeSubmenu.key);
    }
  }

  closeSidebarAfterNavigation() {
    this.returnNavigation.clear();
    this.hideCollapsedTooltip();
    this.sidebarService.setMobileOpen(false);
    this.sidebarService.setExpanded(this.sidebarService.keepExpandedAfterNavigation);
    if (!this.sidebarService.keepExpandedAfterNavigation) {
      this.openSubmenu = null;
    }
  }

  isCollapsedSubmenu(nav: NavItem, key: string) {
    return (
      this.canShowCollapsedSubmenu(nav) &&
      this.openSubmenu === key &&
      !this.sidebarExpanded &&
      !this.sidebarMobileOpen
    );
  }

  isCollapsedSubmenuNav(nav: NavItem) {
    return this.canShowCollapsedSubmenu(nav) && !this.sidebarExpanded && !this.sidebarMobileOpen;
  }

  canShowCollapsedSubmenu(nav: NavItem) {
    return nav.name === 'Audits' || nav.name === 'Maintenance' || nav.name === 'Admin Tools';
  }

  showCollapsedTooltip(event: MouseEvent | FocusEvent, label: string) {
    if (this.sidebarExpanded || this.sidebarMobileOpen) {
      return;
    }

    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return;
    }

    const rect = target.getBoundingClientRect();
    this.collapsedTooltip = {
      label,
      left: rect.right + 12,
      top: rect.top + rect.height / 2,
    };
  }

  hideCollapsedTooltip() {
    this.collapsedTooltip = null;
  }

  private isAuditSubItemActiveForPath(path: string, currentPath: string) {
    const originMatch = currentPath.match(/\/origin\/(planning|confirmed)(?:\/|$)/);
    const origin = originMatch?.[1];

    if (origin === 'planning') {
      return path === '/audit/planning';
    }

    if (origin === 'confirmed') {
      return path === '/audit/confirmed';
    }

    if (currentPath === '/audit/generate' || currentPath.startsWith('/audit/generate/')) {
      return path === '/audit/planning';
    }

    if (currentPath === '/audit/details' || currentPath.startsWith('/audit/details/')) {
      return path === '/audits';
    }

    return currentPath === path || currentPath.startsWith(`${path}/`);
  }

  private withLaunchState(nav: NavItem): NavItem {
    const isDcs = this.auth.user()?.isDcs === true;
    const enabled =
      nav.enabled ??
      (nav.adminOnly
        ? this.auth.canUseAdminTool(nav.path)
        : this.launchPhases.canEnableMenuItem(nav.name, isDcs));

    return {
      ...nav,
      enabled,
      subItems: nav.subItems?.map((subItem) => ({
        ...subItem,
        enabled:
          subItem.enabled ??
          (subItem.adminOnly ? this.auth.canUseAdminTool(subItem.path) : enabled),
      })),
    };
  }

  private canReadNavItem(item: {
    name: string;
    path?: string;
    sectionId?: number;
    maintenanceSection?: MaintenancePermissionSection;
    adminOnly?: boolean;
  }) {
    if (item.adminOnly) {
      return this.auth.canUseAdminTool(item.path);
    }

    if (item.maintenanceSection) {
      return this.auth.canReadMaintenanceSection(item.maintenanceSection);
    }

    return item.sectionId
      ? this.auth.canReadSection(item.sectionId)
      : this.auth.canReadMenuItem(item.name, item.path);
  }

  private refreshOpenSubmenuHeight() {
    if (typeof this.openSubmenu === 'string') {
      this.refreshSubmenuHeight(this.openSubmenu);
    }
  }

  private refreshSubmenuHeight(key: string) {
    if (!this.sidebarExpanded && !this.sidebarMobileOpen) {
      return;
    }

    setTimeout(() => {
      const el = this.browser.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
        this.cdr.detectChanges(); // Ensure UI updates
      }
    });
  }
}
